import type {
    OverUnderRentalAgreementType as RentalAgreementType,
    TenantSummaryUsageType,
    Tenant,
    Rent,
    RentalAgreement,
} from '@einwert/valuation-api-client'

import { z } from 'zod'

const RENTAL_STATUSES = { V: 'rented', L: 'unrented', E: 'self_used' } as const
const RENTAL_AGREEMENT_TYPES = { Frei: 'fixed', Index: 'indexed', Staffel: 'graduated' } as const

export type ParsedTenantImportRow = {
    rowNumber: number
    sourceUsageType: string
    isResidential: boolean
    referenceNumber?: string
    tenantName?: string
    leaseNumber?: string
    floor?: string
    area?: number
    numberOfUnits: number
    note?: string
    rentalStatus: 'rented' | 'unrented' | 'self_used'
    contractualRentMonthly: number
    marketRentPerAreaMonthly?: number
    contractStartDate?: Date
    contractEndDate?: Date
    rentalAgreementType?: RentalAgreementType
}

export type ExternalTenantUsageTypeMapping = Record<string, TenantSummaryUsageType | undefined>
export type ExternalTenantSubUsageTypeMapping = Record<string, string | undefined>

export type TenantImportParseError = {
    message: string
    rowErrors: { rowNumber: number; columns: string[] }[]
}

export const tenantImportRowsSchema = z.array(
    z
        .object({
            rowNumber: z.number(),
            Referenznummer: z.coerce.string().optional(),
            Mietername: z.string().optional(),
            'Mietvertrags-Nr.': z.coerce.string().optional(),
            Etage: z.coerce.string().optional(),
            Nutzung: z.string().min(1),
            'Wohnflaeche oder sonstige Einnahme?': z.string().optional(),
            Status: z.enum(['V', 'L', 'E']),
            Fläche: z.number().optional(),
            Stellplätze: z.number().int().nonnegative().optional(),
            '€ p.M': z.number().optional(),
            Vertragsbeginn: z.date().optional(),
            Vertragsende: z
                .union([z.date(), z.literal('unbefristet')])
                .optional()
                .transform((value) => (value === 'unbefristet' ? undefined : value)),
            Mieterhöhungsmöglichkeit: z.union([z.enum(['Frei', 'Index', 'Staffel']), z.literal('')]).optional(),
            Bemerkung: z.string().optional(),
            'Marktmiete Nebenrechnung': z.number().optional(),
        })
        .superRefine((row, ctx) => {
            if (row.Vertragsbeginn && row.Vertragsende && row.Vertragsende < row.Vertragsbeginn) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['Vertragsende'],
                })
            }
        })
        .transform((row): ParsedTenantImportRow => {
            const isSelfUsedWithoutLease =
                row.Status === 'E' &&
                row['€ p.M'] === undefined &&
                !row.Mieterhöhungsmöglichkeit &&
                !row.Vertragsbeginn &&
                !row.Vertragsende &&
                !row['Mietvertrags-Nr.']

            return {
                rowNumber: row.rowNumber,
                sourceUsageType: row.Nutzung,
                isResidential: row['Wohnflaeche oder sonstige Einnahme?'] === 'Wohnflaeche',
                referenceNumber: row.Referenznummer,
                tenantName: row.Mietername,
                leaseNumber: row['Mietvertrags-Nr.'],
                floor: row.Etage,
                area: row.Fläche,
                numberOfUnits: row.Stellplätze ?? 1,
                note: row.Bemerkung,
                rentalStatus: RENTAL_STATUSES[row.Status],
                contractualRentMonthly: row['€ p.M'] ?? 0,
                marketRentPerAreaMonthly: row['Marktmiete Nebenrechnung'],
                contractStartDate: row.Vertragsbeginn,
                contractEndDate: row.Vertragsende,
                rentalAgreementType: isSelfUsedWithoutLease ? undefined : RENTAL_AGREEMENT_TYPES[row.Mieterhöhungsmöglichkeit || 'Frei'],
            }
        })
)

export type ExternalTransformedTenantType = Partial<
    Omit<Tenant, 'uuid' | 'rent_details' | 'category' | 'rental_agreement'> & {
        market_rent?: Rent
        rental_agreement: Partial<RentalAgreement>
    }
>
